/**
 * Template Editor Dialog
 *
 * Dialog for creating or editing notification templates.
 * Supports multiple localizations per template.
 */

import React, { useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Dropdown } from "primereact/dropdown";
import { Tag } from "primereact/tag";
import { RadioButton } from "primereact/radiobutton";
import { AppCard } from "../../components/ui";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Eye, Trash2 } from "lucide-react";
import {
    templatesApiService,
    eventDefinitionsApiService,
    languagesApiService,
    notificationsApiService,
} from "../../services/api/notifications";
import { operatorsApiService } from "../../services/api/users/OperatorApiService";
import {
    TemplateDto,
    CreateTemplateRequest,
    UpdateTemplateRequest,
    CreateLocalizationRequest,
    UpdateLocalizationRequest,
    EventDefinitionDto,
    LanguageDto,
    NotificationChannel,
    NotificationChannelLabels,
    TemplateStatus,
    TemplateStatusLabels,
    getStatusOptions,
    parseAllowedChannels,
    requiresSubject,
    requiresTitle,
    extractPlaceholders,
    RenderResult,
} from "../../models/notifications";

interface TemplateEditorProps {
    merchantId: number;
    templateId: string | null; // null = create mode
    initialEventCode?: string;
    onSave: () => void;
    onCancel: () => void;
}

interface LocalizationForm {
    localizationId?: string;
    languageId: string;
    subject: string;
    title: string;
    body: string;
}

type LinkMode = "none" | "deepLink" | "externalUrl";

interface FormState {
    eventCode: string;
    channel: NotificationChannel;
    status: TemplateStatus;
    linkMode: LinkMode;
    deepLinkTemplate: string;
    externalUrl: string;
    localizations: LocalizationForm[];
}

const TemplateEditor: React.FC<TemplateEditorProps> = ({
    merchantId,
    templateId,
    initialEventCode,
    onSave,
    onCancel,
}) => {
    const toast = useRef<Toast>(null);
    const isEditMode = templateId !== null;

    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [events, setEvents] = useState<EventDefinitionDto[]>([]);
    const [languages, setLanguages] = useState<LanguageDto[]>([]);
    const [selectedEvent, setSelectedEvent] = useState<EventDefinitionDto | null>(null);
    const [allowedChannels, setAllowedChannels] = useState<NotificationChannel[]>([]);
    const [userEmail, setUserEmail] = useState<string>("");

    const [form, setForm] = useState<FormState>({
        eventCode: initialEventCode ?? "",
        channel: NotificationChannel.Email,
        status: TemplateStatus.Draft,
        linkMode: "none",
        deepLinkTemplate: "",
        externalUrl: "",
        localizations: [],
    });
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Preview state
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewIndex, setPreviewIndex] = useState(0);
    const [previewResult, setPreviewResult] = useState<RenderResult | null>(null);
    const [previewPayload, setPreviewPayload] = useState("{}");

    useEffect(() => {
        loadInitialData();
    }, []);

    useEffect(() => {
        if (form.eventCode && events.length > 0) {
            const event = events.find((e) => e.eventCode === form.eventCode);
            setSelectedEvent(event ?? null);
            if (event) {
                setAllowedChannels(parseAllowedChannels(event.allowedChannels));
            }
        }
    }, [form.eventCode, events]);

    const loadInitialData = async () => {
        setIsLoading(true);

        const [eventsResult, languagesResult, userResult] = await Promise.all([
            eventDefinitionsApiService.getActive(merchantId),
            languagesApiService.getActive(merchantId),
            operatorsApiService.get(),
        ]);

        if (eventsResult.success) {
            setEvents(eventsResult.data);
        }
        if (languagesResult.success) {
            setLanguages(languagesResult.data);
        }
        if (userResult.success) {
            setUserEmail(userResult.data.email);
        }

        if (isEditMode && templateId) {
            const templateResult = await templatesApiService.getById(merchantId, templateId);
            if (templateResult.success) {
                const template = templateResult.data;
                const linkMode: LinkMode = template.externalUrl ? "externalUrl" : template.deepLinkTemplate ? "deepLink" : "none";
                setForm({
                    eventCode: template.eventCode,
                    channel: template.channel,
                    status: template.status,
                    linkMode,
                    deepLinkTemplate: template.deepLinkTemplate ?? "",
                    externalUrl: template.externalUrl ?? "",
                    localizations: template.localizations.map((loc) => ({
                        localizationId: loc.localizationId,
                        languageId: loc.languageId,
                        subject: loc.subject ?? "",
                        title: loc.title ?? "",
                        body: loc.body,
                    })),
                });
            } else {
                toast.current?.show({
                    severity: "error",
                    summary: "Error",
                    detail: templateResult.message || "Failed to load template",
                    life: 4000,
                });
            }
        } else {
            // Initialize with default localization
            const defaultLang = languagesResult.data?.find((l) => l.isoCode === "en") ?? languagesResult.data?.[0];
            if (defaultLang) {
                setForm((prev) => ({
                    ...prev,
                    linkMode: "none" as LinkMode,
                    deepLinkTemplate: "",
                    externalUrl: "",
                    localizations: [{
                        languageId: defaultLang.languageId,
                        subject: "",
                        title: "",
                        body: "",
                    }],
                }));
            }
        }

        setIsLoading(false);
    };

    const updateForm = <K extends keyof FormState>(field: K, value: FormState[K]) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: "" }));
        }
    };

    const updateLocalization = (index: number, field: keyof LocalizationForm, value: string) => {
        const updated = [...form.localizations];
        updated[index] = { ...updated[index], [field]: value };
        updateForm("localizations", updated);

        // Clear error for this localization field
        const errorKey = `localizations.${index}.${field}`;
        if (errors[errorKey]) {
            setErrors((prev) => ({ ...prev, [errorKey]: "" }));
        }
    };

    const addLocalization = () => {
        const usedLanguageIds = form.localizations.map((l) => l.languageId);
        const availableLang = languages.find((l) => !usedLanguageIds.includes(l.languageId));

        if (!availableLang) {
            toast.current?.show({
                severity: "warn",
                summary: "Warning",
                detail: "All available languages are already added",
                life: 3000,
            });
            return;
        }

        updateForm("localizations", [
            ...form.localizations,
            {
                languageId: availableLang.languageId,
                subject: "",
                title: "",
                body: "",
            },
        ]);
    };

    const removeLocalization = (index: number) => {
        if (form.localizations.length === 1) {
            toast.current?.show({
                severity: "warn",
                summary: "Warning",
                detail: "At least one localization is required",
                life: 3000,
            });
            return;
        }
        const updated = form.localizations.filter((_, i) => i !== index);
        updateForm("localizations", updated);
    };

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!form.eventCode) {
            newErrors.eventCode = "Event is required";
        }

        if (form.localizations.length === 0) {
            newErrors.localizations = "At least one localization is required";
        }

        form.localizations.forEach((loc, i) => {
            if (!loc.languageId) {
                newErrors[`localizations.${i}.languageId`] = "Language is required";
            }
            if (requiresSubject(form.channel) && !loc.subject.trim()) {
                newErrors[`localizations.${i}.subject`] = "Subject is required for Email";
            }
            if (requiresTitle(form.channel) && !loc.title.trim()) {
                newErrors[`localizations.${i}.title`] = "Title is required for Push/Pull";
            }
            if (!loc.body.trim()) {
                newErrors[`localizations.${i}.body`] = "Body is required";
            }
        });

        if (form.linkMode === "externalUrl") {
            const trimmed = form.externalUrl.trim();
            if (!trimmed) {
                newErrors.externalUrl = "External URL is required";
            } else if (trimmed.length > 500) {
                newErrors.externalUrl = "External URL must be 500 characters or less";
            } else {
                try {
                    const parsed = new URL(trimmed);
                    if (parsed.protocol !== "https:") {
                        newErrors.externalUrl = "External URL must use https://";
                    }
                } catch {
                    newErrors.externalUrl = "External URL is not a valid URL";
                }
            }
        }

        // Check for duplicate languages
        const langIds = form.localizations.map((l) => l.languageId);
        const duplicates = langIds.filter((id, i) => langIds.indexOf(id) !== i);
        if (duplicates.length > 0) {
            newErrors.localizations = "Each language can only be used once";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;

        setIsSaving(true);

        try {
            if (isEditMode) {
                const request: UpdateTemplateRequest = {
                    status: form.status,
                    deepLinkTemplate: form.linkMode === "deepLink" ? (form.deepLinkTemplate || undefined) : "",
                    externalUrl: form.linkMode === "externalUrl" ? (form.externalUrl || undefined) : "",
                    updatedBy: userEmail,
                    localizations: form.localizations.map((loc) => ({
                        localizationId: loc.localizationId ?? null,
                        languageId: loc.languageId,
                        subject: loc.subject || undefined,
                        title: loc.title || undefined,
                        body: loc.body,
                    })),
                };

                const result = await templatesApiService.update(merchantId, templateId!, request);
                if (result.success) {
                    toast.current?.show({
                        severity: "success",
                        summary: "Updated",
                        detail: "Template has been updated",
                        life: 3000,
                    });
                    setTimeout(onSave, 500);
                } else {
                    toast.current?.show({
                        severity: "error",
                        summary: "Error",
                        detail: result.message || "Failed to update template",
                        life: 4000,
                    });
                }
            } else {
                const request: CreateTemplateRequest = {
                    merchantId,
                    eventCode: form.eventCode,
                    channel: form.channel,
                    deepLinkTemplate: form.linkMode === "deepLink" ? (form.deepLinkTemplate || undefined) : undefined,
                    externalUrl: form.linkMode === "externalUrl" ? (form.externalUrl || undefined) : undefined,
                    createdBy: userEmail,
                    localizations: form.localizations.map((loc) => ({
                        languageId: loc.languageId,
                        subject: loc.subject || undefined,
                        title: loc.title || undefined,
                        body: loc.body,
                    })),
                };

                const result = await templatesApiService.create(request);
                if (result.success) {
                    toast.current?.show({
                        severity: "success",
                        summary: "Created",
                        detail: "Template has been created",
                        life: 3000,
                    });
                    setTimeout(onSave, 500);
                } else {
                    toast.current?.show({
                        severity: "error",
                        summary: "Error",
                        detail: result.message || "Failed to create template",
                        life: 4000,
                    });
                }
            }
        } finally {
            setIsSaving(false);
        }
    };

    const openPreview = (index: number) => {
        setPreviewIndex(index);
        setPreviewResult(null);
        setPreviewPayload("{}");
        setPreviewVisible(true);
    };

    const runPreview = async () => {
        const loc = form.localizations[previewIndex];
        let payload = {};
        try {
            payload = JSON.parse(previewPayload);
        } catch {
            toast.current?.show({
                severity: "error",
                summary: "Invalid JSON",
                detail: "Preview payload must be valid JSON",
                life: 3000,
            });
            return;
        }

        const result = await notificationsApiService.previewDraft(
            merchantId,
            form.eventCode,
            form.channel,
            loc.subject || undefined,
            loc.title || undefined,
            loc.body,
            payload,
            languages.find((l) => l.languageId === loc.languageId)?.isoCode,
            form.deepLinkTemplate || undefined
        );

        if (result.success) {
            setPreviewResult(result.data);
        } else {
            toast.current?.show({
                severity: "error",
                summary: "Preview Failed",
                detail: result.message || "Failed to render preview",
                life: 4000,
            });
        }
    };

    // Options
    const eventOptions = events.map((e) => ({
        label: `${e.eventCode} - ${e.description || "No description"}`,
        value: e.eventCode,
    }));

    const channelOptions = allowedChannels.map((ch) => ({
        label: NotificationChannelLabels[ch],
        value: ch,
    }));

    const getLanguageOptions = (currentLangId: string) => {
        const usedIds = form.localizations.map((l) => l.languageId).filter((id) => id !== currentLangId);
        return languages
            .filter((l) => !usedIds.includes(l.languageId))
            .map((l) => ({
                label: `${l.name} (${l.isoCode})`,
                value: l.languageId,
            }));
    };

    const footer = (
        <div className="flex justify-end gap-2">
            <Button
                label="Cancel"
                icon="pi pi-times"
                outlined
                onClick={onCancel}
                disabled={isSaving}
            />
            <Button
                label={isEditMode ? "Update" : "Create"}
                icon="pi pi-check"
                onClick={handleSave}
                loading={isSaving}
            />
        </div>
    );

    if (isLoading) {
        return (
            <Dialog
                header="Loading..."
                visible
                style={{ width: "800px" }}
                modal
                onHide={onCancel}
            >
                <div className="flex justify-center p-8">
                    <i className="pi pi-spin pi-spinner text-4xl text-blue-500" />
                </div>
            </Dialog>
        );
    }

    return (
        <>
            <Toast ref={toast} position="bottom-center" />
            <Dialog
                header={isEditMode ? "Edit Template" : "Create Template"}
                visible
                style={{ width: "900px" }}
                modal
                onHide={onCancel}
                footer={footer}
                pt={{
                    content: { className: "overflow-y-auto max-h-[75vh]" },
                }}
            >
                <div className="space-y-4">
                    {/* Event & Channel Selection */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-semibold mb-1">
                                Event <span className="text-red-500">*</span>
                            </label>
                            <Dropdown
                                value={form.eventCode}
                                options={eventOptions}
                                onChange={(e) => {
                                    updateForm("eventCode", e.value);
                                    // Reset channel if not allowed
                                    const event = events.find((ev) => ev.eventCode === e.value);
                                    if (event) {
                                        const channels = parseAllowedChannels(event.allowedChannels);
                                        if (!channels.includes(form.channel)) {
                                            updateForm("channel", channels[0]);
                                        }
                                    }
                                }}
                                placeholder="Select Event"
                                className={`w-full ${errors.eventCode ? "p-invalid" : ""}`}
                                disabled={isEditMode}
                                filter
                            />
                            {errors.eventCode && (
                                <small className="p-error block mt-1">{errors.eventCode}</small>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-semibold mb-1">
                                Channel <span className="text-red-500">*</span>
                            </label>
                            <Dropdown
                                value={form.channel}
                                options={channelOptions}
                                onChange={(e) => updateForm("channel", e.value)}
                                placeholder="Select Channel"
                                className="w-full"
                                disabled={isEditMode || !form.eventCode}
                            />
                        </div>

                        {isEditMode && (
                            <div>
                                <label className="block text-sm font-semibold mb-1">Status</label>
                                <Dropdown
                                    value={form.status}
                                    options={getStatusOptions()}
                                    onChange={(e) => updateForm("status", e.value)}
                                    className="w-full"
                                />
                            </div>
                        )}
                    </div>

                    {/* Link Type Toggle (Push/Pull only) */}
                    {(form.channel === NotificationChannel.Push || form.channel === NotificationChannel.Pull) && (
                        <div>
                            <label className="block text-sm font-semibold mb-2">
                                Tap Action
                                <span className="ml-2 font-normal text-gray-500">(optional)</span>
                            </label>
                            <div className="flex flex-wrap gap-4 mb-2">
                                <div className="flex items-center gap-2">
                                    <RadioButton
                                        inputId="linkMode-none"
                                        value="none"
                                        checked={form.linkMode === "none"}
                                        onChange={() => {
                                            updateForm("linkMode", "none");
                                            updateForm("deepLinkTemplate", "");
                                            updateForm("externalUrl", "");
                                        }}
                                    />
                                    <label htmlFor="linkMode-none" className="cursor-pointer">None</label>
                                </div>
                                <div className="flex items-center gap-2">
                                    <RadioButton
                                        inputId="linkMode-deepLink"
                                        value="deepLink"
                                        checked={form.linkMode === "deepLink"}
                                        onChange={() => {
                                            updateForm("linkMode", "deepLink");
                                            updateForm("externalUrl", "");
                                        }}
                                    />
                                    <label htmlFor="linkMode-deepLink" className="cursor-pointer">Deep Link</label>
                                </div>
                                <div className="flex items-center gap-2">
                                    <RadioButton
                                        inputId="linkMode-externalUrl"
                                        value="externalUrl"
                                        checked={form.linkMode === "externalUrl"}
                                        onChange={() => {
                                            updateForm("linkMode", "externalUrl");
                                            updateForm("deepLinkTemplate", "");
                                        }}
                                    />
                                    <label htmlFor="linkMode-externalUrl" className="cursor-pointer">External URL</label>
                                </div>
                            </div>

                            {form.linkMode === "deepLink" && (
                                <div>
                                    <InputText
                                        value={form.deepLinkTemplate}
                                        onChange={(e) => updateForm("deepLinkTemplate", e.target.value)}
                                        placeholder="e.g., myapp://orders/{{orderId}}/details"
                                        className="w-full font-mono text-sm"
                                        maxLength={500}
                                    />
                                    <small className="text-gray-500 block mt-1">
                                        In-app navigation URL with {"{{placeholders}}"}.
                                        {form.deepLinkTemplate && ` (${form.deepLinkTemplate.length}/500 characters)`}
                                    </small>
                                </div>
                            )}

                            {form.linkMode === "externalUrl" && (
                                <div>
                                    <InputText
                                        value={form.externalUrl}
                                        onChange={(e) => updateForm("externalUrl", e.target.value)}
                                        placeholder="e.g., https://example.com/promo/{{promoId}}"
                                        className={`w-full font-mono text-sm ${errors.externalUrl ? "p-invalid" : ""}`}
                                        maxLength={500}
                                    />
                                    {errors.externalUrl ? (
                                        <small className="p-error block mt-1">{errors.externalUrl}</small>
                                    ) : (
                                        <small className="text-gray-500 block mt-1">
                                            Must start with https://. Opens in the system browser on tap. Supports {"{{placeholders}}"}.
                                            {form.externalUrl && ` (${form.externalUrl.length}/500 characters)`}
                                        </small>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Allowed Variables Info */}
                    {selectedEvent && selectedEvent.allowedVariables && selectedEvent.allowedVariables.length > 0 && (
                        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="text-sm font-semibold text-blue-700 mb-2">
                                Available Placeholders for {selectedEvent.eventCode}
                            </div>
                            <div className="flex flex-wrap gap-1">
                                {selectedEvent.allowedVariables.map((v) => (
                                    <Tag
                                        key={v}
                                        value={`{{${v}}}`}
                                        className="font-mono text-xs bg-blue-100 text-blue-700 cursor-pointer"
                                        onClick={() => navigator.clipboard.writeText(`{{${v}}}`)}
                                    />
                                ))}
                            </div>
                            <small className="text-blue-600 mt-1 block">Click to copy</small>
                        </div>
                    )}

                    {/* Localizations */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-semibold">
                                Localizations <span className="text-red-500">*</span>
                            </label>
                            <Button
                                icon="pi pi-plus"
                                label="Add Language"
                                outlined
                                size="small"
                                onClick={addLocalization}
                                disabled={form.localizations.length >= languages.length}
                            />
                        </div>
                        {errors.localizations && (
                            <small className="p-error block mb-2">{errors.localizations}</small>
                        )}

                        <Accordion multiple activeIndex={form.localizations.map((_, i) => i)}>
                            {form.localizations.map((loc, index) => {
                                const lang = languages.find((l) => l.languageId === loc.languageId);
                                return (
                                    <AccordionTab
                                        key={index}
                                        header={
                                            <div className="flex items-center gap-2">
                                                <span>{lang?.name || "Select Language"}</span>
                                                {lang && <Tag value={lang.isoCode} className="text-xs" />}
                                            </div>
                                        }
                                    >
                                        <div className="space-y-3">
                                            <div className="flex items-end gap-3">
                                                <div className="flex-1">
                                                    <label className="block text-sm font-semibold mb-1">
                                                        Language <span className="text-red-500">*</span>
                                                    </label>
                                                    <Dropdown
                                                        value={loc.languageId}
                                                        options={getLanguageOptions(loc.languageId)}
                                                        onChange={(e) => updateLocalization(index, "languageId", e.value)}
                                                        placeholder="Select Language"
                                                        className={`w-full ${errors[`localizations.${index}.languageId`] ? "p-invalid" : ""}`}
                                                    />
                                                    {errors[`localizations.${index}.languageId`] && (
                                                        <small className="p-error">{errors[`localizations.${index}.languageId`]}</small>
                                                    )}
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => openPreview(index)}
                                                    disabled={!loc.body}
                                                    className="flex-shrink-0 p-1.5 rounded-md text-neutral-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:text-primary-400 dark:hover:bg-primary-900/20 transition-colors disabled:opacity-30 disabled:pointer-events-none"
                                                    title="Preview"
                                                >
                                                    <Eye size={15} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => removeLocalization(index)}
                                                    disabled={form.localizations.length === 1}
                                                    className="flex-shrink-0 p-1.5 rounded-md text-neutral-400 hover:text-danger-600 hover:bg-danger-50 dark:hover:text-danger-400 dark:hover:bg-danger-900/20 transition-colors disabled:opacity-30 disabled:pointer-events-none"
                                                    title="Remove localization"
                                                >
                                                    <Trash2 size={15} />
                                                </button>
                                            </div>

                                            {requiresSubject(form.channel) && (
                                                <div>
                                                    <label className="block text-sm font-semibold mb-1">
                                                        Subject <span className="text-red-500">*</span>
                                                    </label>
                                                    <InputText
                                                        value={loc.subject}
                                                        onChange={(e) => updateLocalization(index, "subject", e.target.value)}
                                                        placeholder="Email subject line..."
                                                        className={`w-full ${errors[`localizations.${index}.subject`] ? "p-invalid" : ""}`}
                                                        maxLength={200}
                                                    />
                                                    {errors[`localizations.${index}.subject`] && (
                                                        <small className="p-error">{errors[`localizations.${index}.subject`]}</small>
                                                    )}
                                                </div>
                                            )}

                                            {requiresTitle(form.channel) && (
                                                <div>
                                                    <label className="block text-sm font-semibold mb-1">
                                                        Title <span className="text-red-500">*</span>
                                                    </label>
                                                    <InputText
                                                        value={loc.title}
                                                        onChange={(e) => updateLocalization(index, "title", e.target.value)}
                                                        placeholder="Notification title..."
                                                        className={`w-full ${errors[`localizations.${index}.title`] ? "p-invalid" : ""}`}
                                                        maxLength={100}
                                                    />
                                                    {errors[`localizations.${index}.title`] && (
                                                        <small className="p-error">{errors[`localizations.${index}.title`]}</small>
                                                    )}
                                                </div>
                                            )}

                                            <div>
                                                <label className="block text-sm font-semibold mb-1">
                                                    Body <span className="text-red-500">*</span>
                                                </label>
                                                <InputTextarea
                                                    value={loc.body}
                                                    onChange={(e) => updateLocalization(index, "body", e.target.value)}
                                                    placeholder="Notification body content..."
                                                    rows={4}
                                                    className={`w-full ${errors[`localizations.${index}.body`] ? "p-invalid" : ""}`}
                                                    maxLength={2000}
                                                />
                                                {errors[`localizations.${index}.body`] && (
                                                    <small className="p-error">{errors[`localizations.${index}.body`]}</small>
                                                )}
                                                <div className="flex justify-between mt-1">
                                                    <small className="text-gray-500">
                                                        {loc.body.length}/2000 characters
                                                    </small>
                                                    {loc.body && (
                                                        <small className="text-gray-500">
                                                            Placeholders: {extractPlaceholders(loc.body).join(", ") || "None"}
                                                        </small>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </AccordionTab>
                                );
                            })}
                        </Accordion>
                    </div>
                </div>
            </Dialog>

            {/* Preview Dialog */}
            <Dialog
                header="Preview Template"
                visible={previewVisible}
                style={{ width: "600px" }}
                modal
                onHide={() => setPreviewVisible(false)}
            >
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-semibold mb-1">
                            Test Payload (JSON)
                        </label>
                        <InputTextarea
                            value={previewPayload}
                            onChange={(e) => setPreviewPayload(e.target.value)}
                            rows={4}
                            className="w-full font-mono text-sm"
                            placeholder='{"amount": "50.00", "currency": "BGN"}'
                        />
                    </div>
                    <Button
                        label="Render Preview"
                        icon="pi pi-eye"
                        onClick={runPreview}
                    />

                    {previewResult && (
                        <div className="mt-4 p-4 bg-slate-50 rounded-lg border">
                            <div className="font-semibold mb-2">Rendered Output</div>
                            {previewResult.renderedSubject && (
                                <div className="mb-2">
                                    <span className="text-sm text-gray-600">Subject: </span>
                                    <span className="font-medium">{previewResult.renderedSubject}</span>
                                </div>
                            )}
                            {previewResult.renderedTitle && (
                                <div className="mb-2">
                                    <span className="text-sm text-gray-600">Title: </span>
                                    <span className="font-medium">{previewResult.renderedTitle}</span>
                                </div>
                            )}
                            <div>
                                <span className="text-sm text-gray-600">Body:</span>
                                <pre className="mt-1 p-3 bg-white rounded border whitespace-pre-wrap text-sm">
                                    {previewResult.renderedBody}
                                </pre>
                            </div>

                            {previewResult.renderedDeepLink && (
                                <div className="mt-2">
                                    <span className="text-sm text-gray-600">Deep Link: </span>
                                    <code className="font-mono text-sm text-blue-600 bg-blue-50 px-2 py-1 rounded">
                                        {previewResult.renderedDeepLink}
                                    </code>
                                </div>
                            )}

                            {previewResult.unresolvedPlaceholders.length > 0 && (
                                <div className="mt-3 p-2 bg-amber-50 rounded border border-amber-200">
                                    <div className="text-sm font-semibold text-amber-700">Unresolved Placeholders</div>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                        {previewResult.unresolvedPlaceholders.map((p) => (
                                            <Tag key={p} value={p} severity="warning" className="text-xs" />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {previewResult.warnings.length > 0 && (
                                <div className="mt-3 p-2 bg-amber-50 rounded border border-amber-200">
                                    <div className="text-sm font-semibold text-amber-700">Warnings</div>
                                    <ul className="text-sm text-amber-700 mt-1 list-disc list-inside">
                                        {previewResult.warnings.map((w, i) => (
                                            <li key={i}>{w}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </Dialog>
        </>
    );
};

export default TemplateEditor;
